import React, { useState, useEffect } from 'react';
import { Grid, Container, Typography, Box, CircularProgress, TablePagination, IconButton, TextField, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { FirstPageIcon, LastPageIcon, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';

import { useQueryClient } from 'react-query';
import { SelectRole } from '../../components/filters';

import { Card } from '../../sections/@dashboard/find-edifier';
import { getUserDetails, sortByLname } from '../../utils';
import { useMutationManageCredentials, useQueryGetUsersList } from '../../hooks/react-query/useData';

import { ManageCredentialsDialog, UserDetailedInformationDialog } from '../../components/dialog';
import { useQueryGetSchoolsList } from '../../hooks/react-query/schools';
import CustomPagination from '../../components/pagination/CustomPagination';

const ManageCredentials = () => {
  const userDetails = getUserDetails();

  const userToken = userDetails.token;

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [manageCredentialsOpen, setManageCredentialsOpen] = useState(false);

  const [displayUserDetails, setDisplayUserDetails] = useState(false);

  const [currentUser, setCurrentUser] = useState({});

  const [userToEdit, setUserToEdit] = useState({});
  const [editedUserData, setEditedUserData] = useState({});

  const [currentSelectedFilter, setCurrentSelectedFilter] = useState('All');
  const [schoolSelectedFilter, setSchoolSelectedFilter] = useState('All');

  const [schools, setSchools] = useState({});

  const [selectedId, setSelectedId] = useState('');

  // Search state
  const [searchedValue, setSearchedValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleClose = () => {
    setManageCredentialsOpen(false);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter change handlers to reset pagination
  const handleRoleFilterChange = (newFilter) => {
    setCurrentSelectedFilter(newFilter);
    setPage(0); // Reset to first page when filter changes
  };

  const handleSchoolFilterChange = (newFilter) => {
    setSchoolSelectedFilter(newFilter);
    setPage(0); // Reset to first page when filter changes
  };

  const handleSchoolIdChange = (schoolId) => {
    setSelectedId(schoolId);
  };

  // Search functions
  const searchFromRecord = () => {
    setSearchQuery(searchedValue);
    setPage(0); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchedValue('');
    setSearchQuery('');
    setPage(0);
  };
  const allRoles = [
    { _id: 0, name: 'All' },
    { _id: 1, name: 'Edifiers' },
    { _id: 2, name: 'Teachers' },
    { _id: 3, name: 'Site Leaders' }
  ];
  const { data: coachesList, isFetching: isLoading } = useQueryGetUsersList(
    { 
      token: userDetails?.token, 
      userRole: currentSelectedFilter === 'Edifiers' ? 'COACH' : 
            currentSelectedFilter === 'Teachers' ? 'TEACHER' : 
            currentSelectedFilter === 'Site Leaders' ? 'SITE-LEADER' : null,
      page: page + 1, // API uses 1-based pagination
      limit: rowsPerPage,
      ...(searchQuery && { search: searchQuery }), // Add search parameter
      ...(schoolSelectedFilter !== 'All' && selectedId && {
        schoolId: selectedId
      }) // Add school filter
    },
    navigate,
    toast
  );

  const { data: schoolList } = useQueryGetSchoolsList();
  const { isLoading: isManageCredentialsLoading, mutate: manageCredentials } = useMutationManageCredentials(
    editedUserData,
    toast,
    handleClose,
    userToken,
    setUserToEdit,
    navigate,
    queryClient
  );

  useEffect(() => {
    if (Object.keys(editedUserData).length > 0) {
      manageCredentials();
    }
  }, [editedUserData, manageCredentials]);

  useEffect(() => {
    if (Array.isArray(schoolList?.data?.data)) {
      setSchools([...schoolList.data.data, { _id: 3, name: 'All' }]);
    } else {
      setSchools([{ _id: 3, name: 'All' }]);
    }
  }, [schoolList]);

  const userCard = (index, list) => (
    <Card
      key={index}
      data={list}
      index={index}
      setUserToEdit={setUserToEdit}
      setDisplayUserDetails={setDisplayUserDetails}
      manageCredentials
      setManageCredentials={setManageCredentialsOpen}
      setCurrentUser={setCurrentUser}
      userRole={userDetails.role}
    />
  );

  const filterBaseResults = (index, list) => {
    if (currentSelectedFilter === 'All') {
      if (schoolSelectedFilter === 'All') {
        return userCard(index, list);
      }
      if (list?.schoolId?.includes(selectedId)) {
        return userCard(index, list);
      }
    }
    if (currentSelectedFilter === 'Edifiers' && list?.role === 'COACH') {
      if (schoolSelectedFilter === 'All') {
        return userCard(index, list);
      }
      if (list?.schoolId?.includes(selectedId)) {
        return userCard(index, list);
      }
    }
    if (currentSelectedFilter === 'Teachers' && list?.role === 'TEACHER') {
      if (schoolSelectedFilter === 'All') {
        return userCard(index, list);
      }
      if (list?.schoolId?.includes(selectedId)) {
        return userCard(index, list);
      }
    }
    return null;
  };

  
  return (
    <Box>
      <Helmet>
        <title>Edifying Teachers | Manage Credentials</title>
      </Helmet>
      {userToEdit && <ManageCredentialsDialog
        userToEdit={userToEdit}
        open={Object.keys(userToEdit).length > 0 && manageCredentialsOpen}
        handleClose={handleClose}
        manageCredentials={manageCredentials}
        isLoading={isManageCredentialsLoading}
        setEditedUserData={setEditedUserData}
        toast={toast}
      />}

      <UserDetailedInformationDialog
        open={displayUserDetails}
        handleClose={() => setDisplayUserDetails(false)}
        userData={currentUser}
      />

      <Container maxWidth="xl" sx={{ p: '0 !important' }}>
        {/* <Toaster position="top-right" reverseOrder={false} /> */}

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h3" sx={{ mb: '5px', color: 'text.titles' }}>
            Manage Credentials
          </Typography>

          <Box sx={{ display: 'flex', columnGap: '20px', alignItems: 'center' }}>
            {schools.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ 
                  color: '#666666', 
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}>
                  Partnership Site
                </Typography>
                <SelectRole
                  allRoles={schools}
                  currentSelectedFilter={schoolSelectedFilter}
                  setCurrentSelectedFilter={handleSchoolFilterChange}
                  setSelectedId={handleSchoolIdChange}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ 
                color: '#666666', 
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                Role
              </Typography>
              <SelectRole
                allRoles={allRoles}
                currentSelectedFilter={currentSelectedFilter}
                setCurrentSelectedFilter={handleRoleFilterChange}
                setSelectedId={() => { }} // Not needed for role filter
              />
            </Box>
          </Box>
        </Box>

        {/* Search Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 2,
          mb: 2
        }}>
          <Box sx={{ 
            display: 'flex',
            columnGap: '17px',
            flexDirection: { xs: 'column', md: 'row' },
            rowGap: '20px',
            alignItems: 'center'
          }}>
            <TextField
              placeholder="Search Users by Name"
              className="filters"
              size="small"
              value={searchedValue}
              sx={{ 
                minWidth: '300px', 
                maxWidth: { xs: '100%', md: '300px' },
                '& .MuiInputBase-root': {
                  height: '40px'
                },
                '& .MuiInputBase-input': {
                  height: '40px',
                  py: 0
                }
              }}
              onChange={(e) => setSearchedValue(e.target.value.toLowerCase())}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  searchFromRecord();
                }
              }}
            />

            <Button
              variant="contained"
              color="main"
              size="small"
              onClick={() => searchFromRecord()}
              sx={{ 
                width: 'max-content',
                height: '40px',
                minHeight: '40px',
                maxHeight: '40px',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Search
            </Button>

            {(searchQuery || searchedValue) && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={clearSearch}
                sx={{ 
                  width: 'max-content',
                  height: '40px',
                  minHeight: '40px',
                  maxHeight: '40px',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>

        {!isLoading && (!coachesList?.data?.data?.users || coachesList?.data?.data?.users?.length === 0) && (
          <Box
            sx={{
              width: '100%',
              maxWidth: '965px',
              borderRadius: '20px',
              bgcolor: 'background.paper',
              mx: 'auto',
              minWidth: '100%',
              mt: '30px',
              boxShadow: (theme) => theme.customShadows.card,
              height: { xs: 'max-content', md: '88px' },
              p: { xs: '20px', md: '0 30px' },
              display: 'grid',
              alignItems: 'center',
            }}
          >
            <Typography variant="h3" sx={{ color: 'text.titles', textAlign: 'center', py: '20px' }}>
              No Record Found
            </Typography>
          </Box>
        )}

        {isLoading && (
          <Box sx={{ textAlign: 'center', mt: '30px' }}>
            <CircularProgress />
          </Box>
        )}
        <Grid container spacing={'28.47px'} sx={{ mt: '20px' }}>
          {coachesList?.data?.data &&
            sortByLname(coachesList?.data?.data?.users)?.map((list, index) => filterBaseResults(index, list))}
        </Grid>

        {/* Pagination Controls */}
        {coachesList?.data?.data?.users && coachesList?.data?.data?.users?.length > 0 && (
          <Box sx={{ mt: '20px' }}>
            <CustomPagination
              count={coachesList?.data?.data?.pagination?.totalCount || coachesList?.data?.data?.users?.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Profiles per page:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ManageCredentials;
